/**
 * PackageSaveDialog — Save Config on a package-attributed campaign whose
 * formulas/rules changed: push the edits into the package for everyone,
 * keep them campaign-local (badge gains the ✎ mark), or copy them into a
 * brand-new package that only this campaign follows. Copy is written for
 * the OPERATOR: plain verbs, a who-is-affected pill per option.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useState, type FC } from 'react';
import { GitBranch, PenLine, Users } from 'lucide-react';

import { Button } from '@/components/ui/layout/Button';
import ModalCard from '@/components/ui/layout/ModalCard';
import { Input } from '@/components/ui/form/input';

import ActionRow from '../ActionRow';

export interface PackageSaveDialogProps {
  open: boolean;
  packageName: string;
  /** Campaigns currently assigned to the package (incl. this one). */
  campaignCount: number;
  busy: boolean;
  onUpdateEveryone: () => void;
  onCampaignOnly: () => void;
  onFork: (name: string) => void;
  onCancel: () => void;
}

const PackageSaveDialog: FC<PackageSaveDialogProps> = (props) => {
  const { open, packageName, campaignCount, busy, onCancel } = props;
  const [forkName, setForkName] = useState<string | null>(null);
  const others = Math.max(0, campaignCount - 1);
  const subtitle =
    others > 0
      ? `You edited "${packageName}" — a package that also runs on ${others} other campaign${others === 1 ? '' : 's'}.`
      : `You edited "${packageName}". No other campaign uses this package right now.`;

  return (
    <ModalCard
      open={open}
      title="Where should your changes go?"
      subtitle={subtitle}
      widthClass="max-w-md"
      zClass="z-[160]"
      busy={busy}
      onClose={onCancel}
    >
      {forkName === null ? (
        <div className="space-y-2">
          <ActionRow
            icon={<Users className="h-4 w-4" aria-hidden />}
            title="Update the package for everyone"
            scope={`${campaignCount} campaign${campaignCount === 1 ? '' : 's'}`}
            caption={`Everyone loading "${packageName}" gets your version from now on. This campaign stays in sync with it.`}
            primary
            disabled={busy}
            onClick={props.onUpdateEveryone}
          />
          <ActionRow
            icon={<PenLine className="h-4 w-4" aria-hidden />}
            title="Save just for this campaign"
            scope="only here"
            caption={
              <>
                {`"${packageName}" stays unchanged for everyone else. This campaign gets a `}
                <PenLine
                  className="inline h-3 w-3 text-amber-500"
                  aria-label="edited"
                />
                {' mark on its badge because it no longer matches the package.'}
              </>
            }
            disabled={busy}
            onClick={props.onCampaignOnly}
          />
          <ActionRow
            icon={<GitBranch className="h-4 w-4" aria-hidden />}
            title="Create a new package from my changes…"
            scope="new package"
            caption={`Saves your version as its own package and switches this campaign to it. "${packageName}" is not touched.`}
            disabled={busy}
            onClick={() => setForkName(`${packageName} (edited)`)}
          />
          <div className="flex justify-end pt-1">
            <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <label className="block space-y-1 text-xs font-medium">
            Name the new package
            <Input
              value={forkName}
              onChange={(e) => setForkName(e.target.value)}
              className="mt-1"
              maxLength={120}
              autoFocus
            />
          </label>
          <p className="text-[11px] text-muted-foreground">
            It gets its own formula &amp; rule sets — “{forkName || '…'} —
            formulas” and “{forkName || '…'} — rules”.
          </p>
          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setForkName(null)}
              disabled={busy}
            >
              Back
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={busy || !forkName.trim()}
              onClick={() => props.onFork(forkName.trim())}
            >
              {busy ? 'Saving…' : 'Create & save'}
            </Button>
          </div>
        </div>
      )}
    </ModalCard>
  );
};

export default PackageSaveDialog;
